import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, ChevronLeft, ChevronRight } from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	listApiKeyUsage,
	type ApiKey,
	type ApiKeyUsageEntry,
} from "@/services/api-key.service";

const PAGE_SIZE = 25;

function statusClass(code: number): string {
	if (code >= 500) return "bg-danger-subtle text-danger border-danger/20";
	if (code >= 400) return "bg-warning-subtle text-warning border-warning/20";
	return "bg-success-subtle text-success border-success/20";
}

export function ApiKeyUsageDialog({
	apiKey,
	onClose,
}: {
	apiKey: ApiKey | null;
	onClose: () => void;
}) {
	const [entries, setEntries] = useState<ApiKeyUsageEntry[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);

	// Reset to the first page whenever a different key is opened, so a
	// previously-viewed page number does not leak into the new key's history.
	useEffect(() => {
		if (apiKey) setPage(1);
	}, [apiKey?.id]);

	useEffect(() => {
		if (!apiKey) return;
		let cancelled = false;
		setLoading(true);
		listApiKeyUsage(apiKey.id, page, PAGE_SIZE)
			.then((res) => {
				if (cancelled) return;
				setEntries(res.data);
				setTotal(res.total);
			})
			.catch((err) => {
				if (!cancelled)
					toast.error(err?.message || "Failed to load usage history.");
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [apiKey?.id, page]);

	const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

	return (
		<Dialog open={!!apiKey} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle>Usage history</DialogTitle>
					<DialogDescription>
						Every external API request made with
						{apiKey ? ` "${apiKey.name}"` : " this key"}, newest
						first.
					</DialogDescription>
				</DialogHeader>

				{loading ? (
					<div className="space-y-2">
						<Skeleton className="h-8 w-full" />
						<Skeleton className="h-8 w-full" />
						<Skeleton className="h-8 w-full" />
					</div>
				) : entries.length === 0 ? (
					<div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border p-8 text-center">
						<Activity className="h-6 w-6 text-muted-foreground" />
						<p className="text-sm font-medium text-foreground">
							No requests yet
						</p>
						<p className="max-w-sm text-xs text-muted-foreground">
							This key has not been used. Requests appear here as
							soon as an external app calls the API with it.
						</p>
					</div>
				) : (
					<div className="max-h-[55vh] overflow-auto rounded-lg border border-border">
						<table className="w-full text-left text-xs">
							<thead className="sticky top-0 border-b border-border bg-muted-subtle text-muted-foreground">
								<tr>
									<th className="whitespace-nowrap px-3 py-2 font-medium">
										When
									</th>
									<th className="whitespace-nowrap px-3 py-2 font-medium">
										Request
									</th>
									<th className="whitespace-nowrap px-3 py-2 font-medium">
										Status
									</th>
									<th className="whitespace-nowrap px-3 py-2 font-medium">
										Took
									</th>
									<th className="whitespace-nowrap px-3 py-2 font-medium">
										IP
									</th>
								</tr>
							</thead>
							<tbody>
								{entries.map((e) => (
									<tr
										key={e.id}
										className="border-b border-border last:border-b-0 align-top"
									>
										<td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
											{new Date(
												e.created_at,
											).toLocaleString()}
										</td>
										<td className="px-3 py-2">
											<span className="font-mono text-[11px] font-medium text-foreground">
												{e.method} {e.path}
											</span>
											{e.error_message && (
												<p className="mt-0.5 text-[10px] leading-4 text-danger">
													{e.error_message}
												</p>
											)}
										</td>
										<td className="whitespace-nowrap px-3 py-2">
											<span
												className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${statusClass(e.status_code)}`}
											>
												{e.status_code}
											</span>
										</td>
										<td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
											{e.duration_ms == null
												? "—"
												: `${e.duration_ms} ms`}
										</td>
										<td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-muted-foreground">
											{e.ip ?? "—"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}

				{total > PAGE_SIZE && (
					<div className="flex items-center justify-between">
						<p className="text-xs text-muted-foreground">
							{total} request{total === 1 ? "" : "s"} · page{" "}
							{page} of {lastPage}
						</p>
						<div className="flex gap-2">
							<Button
								type="button"
								variant="outline"
								size="xs"
								disabled={page <= 1 || loading}
								onClick={() => setPage((p) => p - 1)}
							>
								<ChevronLeft className="h-3 w-3" />
								Previous
							</Button>
							<Button
								type="button"
								variant="outline"
								size="xs"
								disabled={page >= lastPage || loading}
								onClick={() => setPage((p) => p + 1)}
							>
								Next
								<ChevronRight className="h-3 w-3" />
							</Button>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
